class StickyProcessSection extends HTMLElement {
  connectedCallback() {
    // Wait for blocks to render
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    this.imageStack = this.querySelector(
      ".sticky-process-section__image-stack"
    );
    this.contentColumn = this.querySelector(
      ".sticky-process-section__content-column"
    );

    if (!this.imageStack || !this.contentColumn) return;

    this.contentItems = this.contentColumn.querySelectorAll(
      ".sticky-process-step__content"
    );
    this.images = this.contentColumn.querySelectorAll(
      ".sticky-process-step__image"
    );

    if (this.contentItems.length === 0 || this.images.length === 0) return;

    // Get index elements if they exist (desktop)
    this.stepTextNumbers = this.querySelectorAll(".step-text-number");
    this.stepCurrents = this.querySelectorAll(".step-current");
    this.stepTotal = this.querySelector("#step-total");
    this.totalSteps = this.contentItems.length;

    // Set initial total if element exists
    if (this.stepTotal) {
      this.stepTotal.textContent = this.totalSteps;
    }

    // Extract images to sticky stack and map to parent block IDs
    this.images.forEach((image, index) => {
      const contentItem = this.contentItems[index];
      if (!contentItem) return;

      const parentBlockId =
        contentItem.getAttribute("data-parent-block-id") || `step-${index}`;

      // Create stacked image container
      const stackedImage = document.createElement("div");
      stackedImage.className = "sticky-process-section__stacked-image";
      stackedImage.setAttribute("data-block-id", parentBlockId);
      stackedImage.appendChild(image.cloneNode(true));
      this.imageStack.appendChild(stackedImage);

      // Remove original image
      image.remove();

      // Add data-block-id to all nested blocks within this content item
      const nestedBlocks = contentItem.querySelectorAll(
        ".process-step__nested-blocks > *"
      );
      nestedBlocks.forEach((nestedBlock) => {
        nestedBlock.setAttribute("data-block-id", parentBlockId);
      });
    });

    // Activate first image
    const stackedImages = this.imageStack.querySelectorAll(
      ".sticky-process-section__stacked-image"
    );
    if (stackedImages.length > 0) {
      stackedImages[0].classList.add("active");
      // Update index display for first step
      this.updateStepIndex(1);
    }

    // Observe nested blocks (individual content blocks) for scroll-based image switching
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const blockId = entry.target.getAttribute("data-block-id");
            if (blockId) this.showImage(blockId);
          }
        });
      },
      {
        rootMargin: "-20% 0px 0% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    // Observe all nested blocks (text, buttons, etc.) instead of content wrappers
    const allNestedBlocks =
      this.contentColumn.querySelectorAll("[data-block-id]");
    allNestedBlocks.forEach((block) => this.observer.observe(block));

    // Initialize mobile indices
    this.initMobileIndices();
  }

  numberToWord(num) {
    const words = [
      "",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
      "twenty",
    ];
    return words[num] || num.toString();
  }

  initMobileIndices() {
    this.contentItems.forEach((contentItem, index) => {
      const stepNumber = index + 1;
      const mobileContainer = contentItem.querySelector(
        ".sticky-process__index-container-mobile"
      );
      if (mobileContainer) {
        const stepTextNumber = mobileContainer.querySelector(
          ".step-text-number-mobile"
        );
        const stepCurrent = mobileContainer.querySelector(
          ".step-current-mobile"
        );
        const stepTotal = mobileContainer.querySelector(".step-total-mobile");

        if (stepTextNumber) {
          stepTextNumber.textContent = this.numberToWord(stepNumber);
        }
        if (stepCurrent) {
          stepCurrent.textContent = stepNumber;
        }
        if (stepTotal) {
          stepTotal.textContent = this.totalSteps;
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  showImage(blockId) {
    const stackedImages = this.imageStack.querySelectorAll(
      ".sticky-process-section__stacked-image"
    );
    let activeIndex = 0;
    stackedImages.forEach((img, index) => {
      const isActive = img.getAttribute("data-block-id") === blockId;
      img.classList.toggle("active", isActive);
      if (isActive) {
        activeIndex = index + 1; // 1-based index
      }
    });

    // Update step index display
    if (activeIndex > 0) {
      this.updateStepIndex(activeIndex);
    }
  }

  updateStepIndex(stepNumber) {
    // Update step text numbers
    this.stepTextNumbers.forEach((el) => {
      const elStep = parseInt(el.getAttribute("data-step"));
      el.classList.toggle("active", elStep === stepNumber);
    });

    // Update step current numbers
    this.stepCurrents.forEach((el) => {
      const elStep = parseInt(el.getAttribute("data-step"));
      el.classList.toggle("active", elStep === stepNumber);
    });
  }
}

if (!customElements.get("sticky-process-section")) {
  customElements.define("sticky-process-section", StickyProcessSection);
}
